/*
  Черновик окна выбора должников: правки, предпросмотр сервером и запись
  (26.10.2026, решение владельца, макет Б2). Сам выбор грузит `AccountActions`
  по нажатию кнопки.

  Состояние и запросы - здесь, разметка - в `ClaimChoiceDialog.jsx` и его
  строках (правило 1 скилла `frontend`: хук знает про сервер, компоненты получают
  всё пропсами).

  Итог «сколько заявлений и к кому» не считается здесь, а спрашивается у
  сервера на каждую правку: его считает то же правовое ядро, что соберёт пакет,
  и окно, посчитавшее его своей копией правил, разошлось бы с пакетом.
*/

import { useCallback, useEffect, useMemo, useState } from 'react'

import { api } from '../api/client.js'

const PREVIEW_DELAY_MS = 250

/*
  Доля в окне вводится дробью, как в заявлении («1/2»), или десятичной
  («0,5»). Серверу уходит число с четырьмя знаками; неразборчивое - пустым, и
  сервер сам назовёт строку, где не указана доля.
*/
export function shareValue(text) {
  const raw = String(text ?? '').trim()
  const fraction = /^(\d+)\s*\/\s*(\d+)$/.exec(raw)
  const value = fraction ? Number(fraction[1]) / Number(fraction[2]) : Number(raw.replace(',', '.'))
  if (!raw || !Number.isFinite(value) || value <= 0 || value > 1) return null
  return value.toFixed(4)
}

function picksOf(people) {
  return Object.fromEntries(
    people.map((person) => [
      person.key,
      {
        claimed: person.claimed,
        mode: person.mode,
        share: person.share_title,
        // Точная доля из базы и то, как она показана в поле (ревью 26.10.2026):
        // поле показывает дробь с ограниченным знаменателем, и без этого каждое
        // сохранение перезаписывало бы 0,1234 её приближением.
        storedShare: person.share,
        shownShare: person.share_title,
        joint_with: person.joint_with,
      },
    ]),
  )
}

/** Доля для сервера: нетронутое поле - точное значение из базы, правленое - разобранное. */
function shareOf(pick) {
  if (pick.mode !== 'share') return null
  if (pick.storedShare && pick.share === pick.shownShare) return pick.storedShare
  return shareValue(pick.share)
}

function bodyOf(picks, asOf) {
  return {
    as_of: asOf || null,
    people: Object.entries(picks).map(([key, pick]) => ({
      key,
      claimed: pick.claimed,
      mode: pick.mode,
      share: shareOf(pick),
      joint_with: pick.mode === 'joint' ? pick.joint_with : null,
    })),
  }
}

/** Черновик окна: правки, предпросмотр сервером и запись целиком. */
export function useClaimDraft(accountId, people, asOf) {
  const [picks, setPicks] = useState(() => picksOf(people))
  const [preview, setPreview] = useState(null)
  const [previewError, setPreviewError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const body = useMemo(() => bodyOf(picks, asOf), [picks, asOf])

  useEffect(() => {
    let current = true
    const timer = setTimeout(() => {
      api
        .previewClaimChoice(accountId, body)
        .then((result) => {
          if (!current) return
          setPreview(result)
          setPreviewError(null)
        })
        .catch((failure) => {
          if (current) setPreviewError(failure)
        })
    }, PREVIEW_DELAY_MS)
    return () => {
      current = false
      clearTimeout(timer)
    }
  }, [accountId, body])

  const change = useCallback((key, patch) => {
    setPicks((all) => ({ ...all, [key]: { ...all[key], ...patch } }))
  }, [])

  async function save() {
    setSaving(true)
    setSaveError(null)
    try {
      return await api.saveClaimChoice(accountId, body)
    } catch (failure) {
      setSaveError(failure)
      return null
    } finally {
      setSaving(false)
    }
  }

  return { picks, change, preview, previewError, save, saving, saveError }
}
